import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Layout Components
import Layout from './components/Layout/Layout';
import PublicLayout from './components/Layout/PublicLayout';

// Auth Components
import Login from './components/Auth/Login';
import Register from './components/Auth/Register';

// Dashboard Components
import StudentDashboard from './components/Dashboard/StudentDashboard';
import InstructorDashboard from './components/Dashboard/InstructorDashboard';
import AdminDashboard from './components/Dashboard/AdminDashboard';

// Feature Components
import CourseList from './components/Courses/CourseList';
import CourseDetail from './components/Courses/CourseDetail';
import CourseMaterials from './components/Courses/CourseMaterials';
import CreateCourse from './components/Courses/CreateCourse';
import EditCourse from './components/Courses/EditCourse';
import CoursePerformance from './components/Courses/CoursePerformance';
import MyEnrollments from './components/Enrollments/MyEnrollments';
import PaymentSuccess from './components/Payments/PaymentSuccess';
import AssignmentList from './components/Assignments/AssignmentList';
import AssignmentDetail from './components/Assignments/AssignmentDetail';
import CreateAssignment from './components/Assignments/CreateAssignment';
import AssignmentSubmissions from './components/Assignments/AssignmentSubmissions';
import AttendanceView from './components/Attendance/AttendanceView';
import GradeView from './components/Grades/GradeView';
import Messages from './components/Messages/Messages';
import Profile from './components/Profile/Profile';
import UserManagement from './components/Admin/UserManagement';
import UserDetails from './components/Admin/UserDetails';
import PaymentManagement from './components/Admin/PaymentManagement';
import AdminSettings from './components/Admin/AdminSettings';
import DocumentUpload from './components/Auth/DocumentUpload';
import InstructorVerification from './components/Admin/InstructorVerification';
import BannerManagement from './components/Admin/BannerManagement';
import AnalyticsPage from './components/Admin/AnalyticsPage';
import ReferAndEarn from './components/ReferAndEarn/ReferAndEarn';

// HomePage no longer used for top-level route; Login is rendered inside PublicLayout for '/'.

// Loading Component
import LoadingSpinner from './components/Common/LoadingSpinner';
import BannerPopup from './components/Common/BannerPopup';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  const ProtectedRoute = ({ children, allowedRoles = [] }) => {
    const location = useLocation();
    if (!user) {
      return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
      return <Navigate to="/dashboard" replace />;
    }

    return children;
  };

  const PublicRoute = ({ children }) => {
    // Prevent update loop: only redirect if not loading and user exists
    if (!loading && user) {
      return <Navigate to="/dashboard" replace />;
    }
    return children;
  };

  const getDashboard = () => {
    switch (user?.role) {
      case 'student':
        return <StudentDashboard />;
      case 'instructor':
        return <InstructorDashboard />;
      case 'admin':
        return <AdminDashboard />;
      default:
        return <Navigate to="/login" replace />;
    }
  };

  return (
    <Router>
      <div className="min-h-screen bg-gray-50">
        <BannerPopup />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={
            <PublicRoute>
              <PublicLayout>
                <Login />
              </PublicLayout>
            </PublicRoute>
          } />
          <Route path="/login" element={
            <PublicRoute>
              <PublicLayout>
                <Login />
              </PublicLayout>
            </PublicRoute>
          } />
          <Route path="/registration" element={
            <PublicRoute>
              <PublicLayout>
                <Register />
              </PublicLayout>
            </PublicRoute>
          } />

          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Layout>
                {getDashboard()}
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/courses" element={
            <ProtectedRoute>
              <Layout>
                <CourseList />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/courses/:id" element={
            <ProtectedRoute>
              <Layout>
                <CourseDetail />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/courses/:id/materials" element={
            <ProtectedRoute allowedRoles={['instructor', 'admin']}>
              <Layout>
                <CourseMaterials />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/courses/:id/performance" element={
            <ProtectedRoute allowedRoles={['instructor', 'admin']}>
              <Layout>
                <CoursePerformance />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/create-course" element={
            <ProtectedRoute allowedRoles={['instructor', 'admin']}>
              <Layout>
                <CreateCourse />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/edit-course/:id" element={
            <ProtectedRoute allowedRoles={['instructor', 'admin']}>
              <Layout>
                <EditCourse />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/my-courses" element={
            <ProtectedRoute allowedRoles={['student']}>
              <Layout>
                <MyEnrollments />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/payment-success" element={
            <ProtectedRoute allowedRoles={['student']}>
              <Layout>
                <PaymentSuccess />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/assignments" element={
            <ProtectedRoute>
              <Layout>
                <AssignmentList />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/assignments/:id" element={
            <ProtectedRoute>
              <Layout>
                <AssignmentDetail />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/assignments/:id/submissions" element={
            <ProtectedRoute allowedRoles={['instructor', 'admin']}>
              <Layout>
                <AssignmentSubmissions />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/create-assignment" element={
            <ProtectedRoute allowedRoles={['instructor']}> {/* Removed admin */}
              <Layout>
                <CreateAssignment />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/attendance" element={
            <ProtectedRoute>
              <Layout>
                <AttendanceView />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/grades" element={
            <ProtectedRoute>
              <Layout>
                <GradeView />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/messages" element={
            <ProtectedRoute>
              <Layout>
                <Messages />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute>
              <Layout>
                <Profile />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/admin/users" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <UserManagement />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/admin/users/:userId" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <UserDetails />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/admin/payments" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <PaymentManagement />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/admin/settings" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <AdminSettings />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/upload-documents" element={
            <ProtectedRoute allowedRoles={['instructor']}>
              <DocumentUpload />
            </ProtectedRoute>
          } />

          <Route path="/admin/instructor-verification" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <InstructorVerification />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/admin/banners" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <BannerManagement />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/admin/analytics" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <Layout>
                <AnalyticsPage />
              </Layout>
            </ProtectedRoute>
          } />

          <Route path="/refer-and-earn" element={
            <ProtectedRoute allowedRoles={['student']}>
              <Layout>
                <ReferAndEarn />
              </Layout>
            </ProtectedRoute>
          } />

          {/* Default Route */}
          <Route path="/login" element={
            user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
          } />

          {/* 404 Route */}
          <Route path="*" element={
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <h1 className="text-4xl font-bold text-gray-900">404</h1>
                <p className="text-gray-600 mt-2">Page not found</p>
              </div>
            </div>
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
